// AuditTrailModal Component - Shows audit trail/history for risk assessments
// frontend/src/components/RiskMapping/AuditTrailModal.tsx

import React, { useState, useEffect } from 'react';
import { X, Clock, User, FileText, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import api from '../../api';

interface AuditEntry {
  id: number;
  riskAssessmentId: number;
  reviewType: string;
  reviewedBy: string;
  reviewDate: string;
  previousValues: any;
  newValues: any;
  reviewComments: string;
  approvalStatus: string;
  createdAt: string;
}

interface AuditTrailModalProps {
  riskAssessmentId: number;
  riskId: string;
  isOpen: boolean;
  onClose: () => void;
}

const AuditTrailModal: React.FC<AuditTrailModalProps> = ({
  riskAssessmentId,
  riskId,
  isOpen,
  onClose
}) => {
  const [auditTrail, setAuditTrail] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && riskAssessmentId) {
      fetchAuditTrail();
    }
  }, [isOpen, riskAssessmentId]);

  const fetchAuditTrail = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/comprehensive-risk-assessment/audit-trail/${riskAssessmentId}`);

      if (response.data?.success) {
        setAuditTrail(response.data.auditTrail || []);
      } else {
        setError('Failed to load audit trail');
      }
    } catch (err: any) {
      console.error('Failed to fetch audit trail:', err);
      setError(err.response?.data?.message || 'Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFieldName = (fieldName: string): string => {
    const fieldMap: Record<string, string> = {
      inherent_probability: 'Likelihood',
      inherent_impact: 'Impact',
      residual_risk_score: 'Risk Score',
      likelihood_rationale: 'Rationale',
      business_translation: 'Business Translation',
      auditor_might_ask: 'Auditor Questions',
      key_evidence_required: 'Key Evidence',
      business_impact_translation: 'Business Impact',
      regulatory_frameworks: 'Regulatory Frameworks',
      review_status: 'Review Status'
    };
    return fieldMap[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getChangedFields = (previousValues: any, newValues: any): string[] => {
    const changed: string[] = [];

    if (!newValues || typeof newValues !== 'object') return changed;

    Object.keys(newValues).forEach(key => {
      if (key === 'updated_at') return; // Skip timestamp

      const oldVal = previousValues?.[key];
      const newVal = newValues[key];

      // Check if value actually changed
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changed.push(key);
      }
    });

    return changed;
  };

  const renderValueChange = (fieldName: string, oldValue: any, newValue: any) => {
    // Handle different value types
    let oldDisplay = oldValue;
    let newDisplay = newValue;

    // Format arrays (like regulatory frameworks)
    if (Array.isArray(oldValue)) {
      oldDisplay = oldValue.join(', ');
    }
    if (Array.isArray(newValue)) {
      newDisplay = newValue.join(', ');
    }

    // Format JSON strings
    if (typeof oldValue === 'string' && oldValue.startsWith('[')) {
      try {
        const parsed = JSON.parse(oldValue);
        oldDisplay = Array.isArray(parsed) ? parsed.join(', ') : oldValue;
      } catch (e) {
        // Keep as is
      }
    }
    if (typeof newValue === 'string' && newValue.startsWith('[')) {
      try {
        const parsed = JSON.parse(newValue);
        newDisplay = Array.isArray(parsed) ? parsed.join(', ') : newValue;
      } catch (e) {
        // Keep as is
      }
    }

    // Truncate long text
    const truncate = (text: any, maxLength: number = 100) => {
      const str = String(text || '-');
      return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
    };

    const isIncrease = typeof newValue === 'number' && typeof oldValue === 'number' && newValue > oldValue;
    const isDecrease = typeof newValue === 'number' && typeof oldValue === 'number' && newValue < oldValue;

    return (
      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-2">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-semibold text-gray-700">{formatFieldName(fieldName)}</span>
          {isIncrease && <TrendingUp className="h-4 w-4 text-red-500" />}
          {isDecrease && <TrendingDown className="h-4 w-4 text-green-500" />}
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-gray-500 mb-1">Previous:</div>
            <div className="text-gray-700 bg-white p-2 rounded border">{truncate(oldDisplay)}</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Updated:</div>
            <div className="text-gray-900 bg-blue-50 p-2 rounded border border-blue-200 font-medium">{truncate(newDisplay)}</div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Clock className="h-6 w-6 text-blue-600" />
              Audit Trail
            </h2>
            <p className="text-sm text-gray-600 mt-1">Change history for Risk ID: <span className="font-mono font-semibold text-purple-600">{riskId}</span></p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Close"
          >
            <X className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {!loading && !error && auditTrail.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No changes recorded yet</p>
              <p className="text-gray-400 text-sm mt-2">This risk assessment hasn't been edited</p>
            </div>
          )}

          {!loading && !error && auditTrail.length > 0 && (
            <div className="space-y-6">
              {/* Timeline */}
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 to-gray-200"></div>

                {auditTrail.map((entry, index) => {
                  const changedFields = getChangedFields(entry.previousValues, entry.newValues);

                  return (
                    <div key={entry.id} className="relative pl-16 pb-8 last:pb-0">
                      {/* Timeline dot */}
                      <div className="absolute left-0 w-12 h-12 bg-blue-100 rounded-full border-4 border-white shadow-md flex items-center justify-center">
                        <FileText className="h-5 w-5 text-blue-600" />
                      </div>

                      {/* Content card */}
                      <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        {/* Header */}
                        <div className="p-4 border-b border-gray-100 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-gray-500" />
                              <span className="font-semibold text-gray-900">{entry.reviewedBy}</span>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              entry.approvalStatus === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {entry.approvalStatus}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Clock className="h-4 w-4" />
                            <span>{formatDate(entry.reviewDate)}</span>
                          </div>
                          {entry.reviewComments && (
                            <div className="mt-2 text-sm text-gray-700 italic">
                              "{entry.reviewComments}"
                            </div>
                          )}
                        </div>

                        {/* Changes */}
                        {changedFields.length > 0 && (
                          <div className="p-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">
                              Changes Made ({changedFields.length})
                            </h4>
                            {changedFields.map(field => (
                              <div key={field}>
                                {renderValueChange(
                                  field,
                                  entry.previousValues?.[field],
                                  entry.newValues?.[field]
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {auditTrail.length > 0 && (
              <span>Total changes: <span className="font-semibold">{auditTrail.length}</span></span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditTrailModal;
